import { Button } from '@material-ui/core';
import { chain } from 'lodash';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { ComboChip } from '../../../../../../../Chip/ComboChip';
import VoxelButton from '../../../../../../../../voxel/Buttons/VoxelButton';
import VoxelText from '../../../../../../../../voxel/Text/VoxelText';
import { useDebounceValue } from '../../../../../../../../../logic/otherUtilities/useDebounceValue';
import { FormCombination, FormWordGroup } from '../models/ComboFormState';
import { SetFormState } from '../models/SetFormState';

export interface ComboSelectedSectionProps {
  wordGroups: FormWordGroup[];
  combinations: FormCombination[];
  setFormState: SetFormState;
  disabled: boolean;
}

const ComboSelectedSection: React.FC<ComboSelectedSectionProps> = React.memo(
  ({ setFormState, combinations, wordGroups, disabled }) => {
    const handleDeselectWordCombination = useCallback((wgC: FormCombination) => {
      setFormState((prevState) => ({
        ...prevState,
        comboCombinations: prevState.comboCombinations.map((cc) => {
          if (cc.id === wgC.id) {
            return { ...cc, selected: false };
          }
          return cc;
        }),
      }));
    }, []);

    const handleDeselectAll = useCallback(() => {
      setFormState((prevState) => ({
        ...prevState,
        comboCombinations: prevState.comboCombinations.map((cc) => {
          return { ...cc, selected: false };
        }),
      }));
    }, []);

    // Debouncing, filtering and slicing
    const debouncedValues = useDebounceValue({ wordGroups, combinations }, 200, [
      wordGroups,
      combinations,
    ]);

    const filteredCombinations = useMemo(
      () =>
        chain(debouncedValues.combinations)
          .orderBy(
            [(cc) => cc.name.replace(/[,\s]+/gm, ''), (cc) => cc.removeSpacing],
            ['asc', 'asc']
          )
          .filter((cc) => cc.selected)
          .value(),
      [debouncedValues.combinations]
    );

    const [showCount, setShowCount] = useState(100);
    const handleShowMore = useCallback(() => setShowCount((prev) => prev + 100), []);

    const finalCombinations = useMemo(
      () => filteredCombinations.slice(0, showCount),
      [filteredCombinations, showCount]
    );

    return (
      <div className="p-4 bg-gray-100 rounded flex flex-col gap-2">
        <VoxelText>Selected combinations</VoxelText>
        <VoxelButton
          size="xs"
          title="Deselect all"
          onClick={() => handleDeselectAll()}
          disabled={disabled}
        />
        <div className="flex flex-row gap-2 flex-wrap">
          {finalCombinations.map((cc) => (
            <ComboChip
              key={cc.id}
              cc={cc}
              wordGroups={debouncedValues.wordGroups}
              handleDeselectWordCombination={handleDeselectWordCombination}
              disabled={disabled}
            />
          ))}
          {showCount < filteredCombinations.length && (
            <Button onClick={handleShowMore}>Load more</Button>
          )}
        </div>
      </div>
    );
  }
);

export default ComboSelectedSection;
